import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { ICarouselImage } from 'src/app/interfaces/carousel-image';
import { IListingDataSourceConfig } from 'src/app/interfaces/listing-data-source-config';
import { BehaviorSubject, Subscription as RxSubscription } from 'rxjs';

import { CarouselImageApiService } from './carousel-image-api-service';

export class CarouselImageDataSource extends DataSource<any> {
  public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private CarouselImage: BehaviorSubject<ICarouselImage[]> =
    new BehaviorSubject([]);
  private currentFeaturedImage;
  private Subscriptions: RxSubscription[] = [];
  public role: string;

  constructor(
    protected DataSourceConfig: IListingDataSourceConfig,
    protected CarouselImageApi: CarouselImageApiService
  ) {
    super();
  }

  public getConfig() {
    return this.DataSourceConfig;
  }

  public load() {
    if (!this.DataSourceConfig.listingId) {
      return;
    }

    this.loading.next(true);
    this.CarouselImageApi.list(
      this.DataSourceConfig.listingId,
      this.DataSourceConfig.role
    )
      .then(({ data }: any) => {
        const { company_image_id } = data.find(
          (item) => item.featured === true
        );
        this.currentFeaturedImage = company_image_id;
        this.CarouselImage.next(data);
        this.loading.next(false);
      })
      .catch((error) => this.loading.next(false));
  }

  public list(role) {
    this.load();

    const ref = this.CarouselImageApi.viewList(this, role);

    this.Subscriptions.push(ref.afterClosed().subscribe(() => this.load()));

    return ref;
  }

  public viewList(role?: string) {
    this.Subscriptions.push(
      this.CarouselImageApi.viewList(this, role)
        .afterClosed()
        .subscribe(() => this.load())
    );
  }

  public save(CarouselImage: ICarouselImage) {
    return new Promise<void>((resolve, reject) => {
      this.CarouselImageApi.save(this.DataSourceConfig.listingId, CarouselImage)
        .then(
          () => this.load(),
          (error: string) => reject(error)
        )
        .then(
          () => resolve(),
          (error: string) => reject(error)
        );
    });
  }

  public setFeatured(CarouselImage: ICarouselImage) {
    return new Promise<void>((resolve, reject) => {
      this.CarouselImageApi.setFeaturedImage(
        this.DataSourceConfig.role,
        this.DataSourceConfig.listingId,
        CarouselImage,
        this.currentFeaturedImage
      )
        .then(
          () => this.load(),
          (error: string) => reject(error)
        )
        .then(
          () => resolve(),
          (error: string) => reject(error)
        );
    });
  }

  public delete(CarouselImage: ICarouselImage) {
    return new Promise<void>((resolve, reject) => {
      this.CarouselImageApi.delete(
        this.DataSourceConfig.role,
        this.DataSourceConfig.listingId,
        CarouselImage
      )
        .then(
          () => this.load(),
          (error: string) => reject(error)
        )
        .then(
          () => resolve(),
          (error: string) => reject(error)
        );
    });
  }

  /**
   * Connects a collection viewer (such as a data-table) to this data source.
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   * @returns Observable that emits a new value when the data changes.
   */
  public connect(collectionViewer?: CollectionViewer) {
    return this.CarouselImage;
  }

  /**
   * Disconnects a collection viewer (such as a data-table) from this data source. Can be used
   * to perform any clean-up or tear-down operations when a view is being destroyed.
   *
   * @param collectionViewer The component that exposes a view over the data provided by this
   *     data source.
   */
  public disconnect(collectionViewer?: CollectionViewer) {
    this.Subscriptions.forEach((Subscription) => Subscription.unsubscribe());
  }
}
